// Supplier component
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ExportTableButton } from "../../../utils/export-document.js";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { printReport } from "../../../utils/print-documents.js";
import { makeNetworkCall } from "../../../utils/fetchData.js";
import { OrdersTable } from "../../../sections/purchaseOrders/purchaseOrders-table";
import { DeliveriesTable } from "../../../sections/deliveries/deliveries-table";
import { PaymentTable } from "../../../sections/payments/payments-table";
import {
  DEFAULT_PER_PAGE,
  SUPPLIER_DELIVERIES_ENDPOINT,
  SUPPLIER_ORDERS_ENDPOINT,
  SUPPLIER_PAYMENTS_ENDPOINT,
} from "../../../utils/constants.js";
import AddPurchaseOrders from "../../../sections/purchaseOrders/add-purchaseOrders.js";

const useSuppliers = (page, rowsPerPage, supplier_id) => {
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [paymentsData, setPaymentsData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [orderTotals, setOrderTotals] = useState({});
  const [deliveryTotals, setDeliveryTotals] = useState({});
  const [paymentTotals, setPaymentTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const payload = {
    offset: page * rowsPerPage,
    per_page: rowsPerPage,
    supplier_id: supplier_id,
  };
  useEffect(() => {
    const fetchData = async () => {
      const formData = new FormData();
      formData.append("offset", page * rowsPerPage);
      formData.append("per_page", rowsPerPage);
      formData.append("supplier_id", Number(supplier_id));
      await makeNetworkCall(
        SUPPLIER_ORDERS_ENDPOINT,
        payload,
        setOrderData,
        setOrderTotals,
        setLoading,
        {}
      );
    };

    fetchData();
  }, [page, rowsPerPage, supplier_id]);
  useEffect(() => {
    const fetchData = async () => {
      await makeNetworkCall(
        SUPPLIER_DELIVERIES_ENDPOINT,
        payload,
        setDeliveriesData,
        setDeliveryTotals,
        setLoading,
        {}
      );
    };

    fetchData();
  }, [page, rowsPerPage, supplier_id]);
  useEffect(() => {
    const formData = new FormData();
    formData.append("offset", page * rowsPerPage);
    formData.append("per_page", rowsPerPage);
    formData.append("supplier_id", Number(supplier_id));

    makeNetworkCall(
      SUPPLIER_PAYMENTS_ENDPOINT,
      payload,
      setPaymentsData,
      setPaymentTotals,
      setLoading,
      {}
    );
  }, [page, rowsPerPage, supplier_id]);

  return {
    loading,
    orderTotals,
    deliveryTotals,
    paymentTotals,
    deliveriesData,
    paymentsData,
    orderData,
  };
};

const Supplier = ({ type }) => {
  const { id, full_name, company_name } = useParams();
  const supplierData = { id, full_name, company_name };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PER_PAGE);
  const {
    loading,
    orderTotals,
    deliveryTotals,
    paymentTotals,
    deliveriesData,
    paymentsData,
    orderData,
  } = useSuppliers(page, rowsPerPage, id);
  const componentRef = useRef();
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(event.target.value);
  }, []);

  return (
    <>
      <Helmet>
        <title>${type} | Procurement </title>
      </Helmet>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
        }}
      >
        <Stack spacing={3} sx>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Stack spacing={1}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <Typography sx={{ fontSize: "13px" }} variant="h6">
                  {full_name}- {company_name}
                </Typography>
                <Button
                  onClick={() =>
                    printReport(
                      componentRef,
                      `${
                        value === 0
                          ? "List of supplier orders"
                          : value === 1
                          ? "List of supplier deliveries"
                          : "List of supplier payments"
                      }`
                    )
                  }
                  color="secondary"
                  startIcon={
                    <SvgIcon fontSize="small">
                      <LocalPrintshopIcon />
                    </SvgIcon>
                  }
                >
                  Print
                </Button>
                <ExportTableButton
                  componentRef={componentRef}
                  pageTittle="Supplier"
                />
              </Stack>
            </Stack>
            <div>
              <AddPurchaseOrders supplierData={supplierData} />
            </div>
          </Stack>

          <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
            <Tabs value={value} onChange={handleChange} centered>
              <Tab label="Orders" />
              <Tab label="Deliveries" />
              <Tab label="Payments" />
            </Tabs>
          </Box>
          <Box>
            {value === 0 && (
              <OrdersTable
                type="lpo"
                totals={orderTotals}
                items={orderData}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                page={page}
                rowsPerPage={rowsPerPage}
                isLoading={loading}
                componentRef={componentRef}
              />
            )}
            {value === 1 && (
              <DeliveriesTable
                totals={deliveryTotals}
                items={deliveriesData}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                page={page}
                rowsPerPage={rowsPerPage}
                isLoading={loading}
                componentRef={componentRef}
              />
            )}
            {value === 2 && (
              <PaymentTable
                totals={paymentTotals}
                items={paymentsData}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                page={page}
                rowsPerPage={rowsPerPage}
                isLoading={loading}
                componentRef={componentRef}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default Supplier;
